import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../lib/api';
import {
  Card,
  Button,
  Input,
  Textarea,
  PageHeader,
  Spinner,
  EmptyState,
  Modal,
  Field,
  Select,
  Badge,
  ErrorBanner,
  formatDate,
} from '../components/ui';

interface Course {
  id: string;
  title: string;
  description?: string;
  status: 'draft' | 'published';
  slug: string;
  createdAt: string;
}

interface CourseDetail extends Course {
  sections: Section[];
}

interface Section {
  id: string;
  title: string;
  position: number;
  lessons: Lesson[];
}

interface Lesson {
  id: string;
  title: string;
  type: 'video' | 'text' | 'quiz' | 'assignment';
  content?: string;
  videoUrl?: string;
  position: number;
}

export function CoursesPage() {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ['courses'],
    queryFn: () => api.get<{ data: Course[] }>('/courses').catch(() => ({ data: [] })),
  });

  const courses = q.data?.data ?? [];

  return (
    <div>
      <PageHeader
        title="Courses"
        subtitle={`${courses.length} courses`}
        action={<Button onClick={() => setShowCreate(true)}>+ New course</Button>}
      />

      {q.isLoading ? (
        <Spinner />
      ) : courses.length === 0 ? (
        <Card>
          <EmptyState
            title="No courses yet"
            description="Build your first course with sections, lessons, and quizzes."
            action={<Button onClick={() => setShowCreate(true)}>+ New course</Button>}
          />
        </Card>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 'var(--space-3)' }}>
          {courses.map((c) => (
            <Card key={c.id} onClick={() => setEditingId(c.id)}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                <h3 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 600 }}>{c.title}</h3>
                <Badge color={c.status === 'published' ? 'green' : 'gray'}>{c.status}</Badge>
              </div>
              {c.description && (
                <p style={{ margin: 'var(--space-2) 0 0', fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' }}>
                  {c.description}
                </p>
              )}
              <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--color-text-tertiary)' }}>
                Created {formatDate(c.createdAt)}
              </div>
            </Card>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateCourseModal
          onClose={() => setShowCreate(false)}
          onSaved={(id) => {
            qc.invalidateQueries({ queryKey: ['courses'] });
            setShowCreate(false);
            setEditingId(id);
          }}
        />
      )}

      {editingId && (
        <CourseEditor
          courseId={editingId}
          onClose={() => setEditingId(null)}
        />
      )}
    </div>
  );
}

function CreateCourseModal({ onClose, onSaved }: { onClose: () => void; onSaved: (id: string) => void }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () =>
      api.post<{ id: string }>('/courses', {
        title,
        description: description || undefined,
        slug: title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
        status: 'draft',
      }),
    onSuccess: (c) => onSaved(c.id),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  return (
    <Modal onClose={onClose} title="New course">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!title) return setError('Title required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Title *">
          <Input value={title} onChange={setTitle} autoFocus />
        </Field>
        <Field label="Description">
          <Textarea value={description} onChange={setDescription} rows={3} />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={mut.isPending}>
            {mut.isPending ? 'Creating…' : 'Create'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function CourseEditor({ courseId, onClose }: { courseId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const [editingLesson, setEditingLesson] = useState<{ sectionId: string; lesson?: Lesson } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const q = useQuery({
    queryKey: ['course-detail', courseId],
    queryFn: () => api.get<CourseDetail>(`/courses/${courseId}`),
  });

  const sectionMut = useMutation({
    mutationFn: (title: string) =>
      api.post(`/courses/${courseId}/sections`, { title, position: (q.data?.sections.length ?? 0) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['course-detail', courseId] }),
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  const publishMut = useMutation({
    mutationFn: () => api.patch(`/courses/${courseId}`, { status: 'published' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['course-detail', courseId] }),
  });

  if (q.isLoading) {
    return (
      <Modal onClose={onClose} title="Loading…">
        <Spinner />
      </Modal>
    );
  }

  const course = q.data!;

  return (
    <Modal onClose={onClose} title={course.title} width={760}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Badge color={course.status === 'published' ? 'green' : 'gray'}>{course.status}</Badge>
          {course.status === 'draft' && (
            <Button onClick={() => publishMut.mutate()} disabled={publishMut.isPending}>
              {publishMut.isPending ? 'Publishing…' : 'Publish'}
            </Button>
          )}
        </div>

        {(course.sections ?? []).map((section) => (
          <Card key={section.id}>
            <h4 style={{ margin: 0, fontSize: 'var(--text-base)', fontWeight: 600 }}>{section.title}</h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 'var(--space-2) 0 0', display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
              {(section.lessons ?? []).map((lesson) => (
                <li
                  key={lesson.id}
                  onClick={() => setEditingLesson({ sectionId: section.id, lesson })}
                  style={{
                    padding: 'var(--space-2)',
                    borderRadius: 'var(--radius-md)',
                    cursor: 'pointer',
                    fontSize: 'var(--text-sm)',
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>
                    {lesson.type === 'video' ? '🎥' : lesson.type === 'text' ? '📄' : lesson.type === 'quiz' ? '❓' : '📝'} {lesson.title}
                  </span>
                  <Badge color="gray">{lesson.type}</Badge>
                </li>
              ))}
            </ul>
            <Button size="sm" variant="ghost" onClick={() => setEditingLesson({ sectionId: section.id })} style={{ marginTop: 'var(--space-2)' }}>
              + Add lesson
            </Button>
          </Card>
        ))}

        <Button
          variant="secondary"
          onClick={() => {
            const title = prompt('Section title?');
            if (title) sectionMut.mutate(title);
          }}
        >
          + Add section
        </Button>

        <ErrorBanner error={error} />
      </div>

      {editingLesson && (
        <LessonModal
          courseId={courseId}
          sectionId={editingLesson.sectionId}
          lesson={editingLesson.lesson}
          onClose={() => setEditingLesson(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['course-detail', courseId] });
            setEditingLesson(null);
          }}
        />
      )}
    </Modal>
  );
}

function LessonModal({
  courseId,
  sectionId,
  lesson,
  onClose,
  onSaved,
}: {
  courseId: string;
  sectionId: string;
  lesson?: Lesson;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState(lesson?.title ?? '');
  const [type, setType] = useState<Lesson['type']>(lesson?.type ?? 'text');
  const [videoUrl, setVideoUrl] = useState(lesson?.videoUrl ?? '');
  const [content, setContent] = useState(lesson?.content ?? '');
  const [error, setError] = useState<string | null>(null);

  const mut = useMutation({
    mutationFn: () => {
      const body = { title, type, videoUrl: videoUrl || undefined, content: content || undefined, sectionId };
      return lesson
        ? api.patch(`/lessons/${lesson.id}`, body)
        : api.post(`/courses/${courseId}/lessons`, body);
    },
    onSuccess: onSaved,
    onError: (e) => setError(e instanceof ApiClientError ? e.message : 'Failed'),
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/lessons/${lesson!.id}`),
    onSuccess: onSaved,
  });

  return (
    <Modal onClose={onClose} title={lesson ? 'Edit lesson' : 'New lesson'} width={640}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!title) return setError('Title required');
          mut.mutate();
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      >
        <Field label="Title *">
          <Input value={title} onChange={setTitle} autoFocus />
        </Field>
        <Field label="Type">
          <Select
            value={type}
            onChange={setType}
            options={[
              { value: 'text', label: 'Text / Reading' },
              { value: 'video', label: 'Video' },
              { value: 'quiz', label: 'Quiz' },
              { value: 'assignment', label: 'Assignment' },
            ]}
          />
        </Field>
        {type === 'video' && (
          <Field label="Video URL">
            <Input value={videoUrl} onChange={setVideoUrl} placeholder="https://…" />
          </Field>
        )}
        <Field label="Content" hint="Markdown supported">
          <Textarea value={content} onChange={setContent} rows={8} />
        </Field>
        <ErrorBanner error={error} />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          {lesson ? (
            <Button variant="danger" onClick={() => deleteMut.mutate()} disabled={deleteMut.isPending}>
              Delete
            </Button>
          ) : (
            <span />
          )}
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              {mut.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
}
